"use client";

import React, { useRef } from "react";
import { useEffect, useState } from "react";
import DetailedReviewContents from "./user-review-contents";
import { useSearchParams, usePathname } from "next/navigation";
import DetailedReviewLightBoxSkeleton from "../skeleton/DetailedReviewLightBoxSkeleton";
import {
  ReviewFilterQueryParamKeys,
  defaultPageNo,
  MENTION_FILTERS,
  REVIEWS_PER_PAGE,
} from "@packages/models/university-course-reviews/review.model";
import {
  getReviewHelpfulDetails,
  getUniversityUserReviews,
} from "./review-api-calls";
import ReviewsPaginations from "./reviews-paginations";
import FilterLabel from "./filter-label";
import ReviewSort from "./review-sort";
import Link from "next/link";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GAConstants } from "../services/constants";

const UserReviewList = ({
  userReviewDetails,
  uniInfo,
  setFilterVisisble,
  commonGAAttrs,
  commonGAAttrsOld,
}: any) => {
  const [userReviews, setUserReviews] = useState(userReviewDetails);
  const [isLoading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const reviewIds =
      userReviewDetails?.reviews?.map((review: any) => review.reviewId) || [];
    if (reviewIds.length > 0) {
      getReviewHelpfulDetails(reviewIds)?.then((helpfulDetails) => {
        const updatedReviews = userReviews?.reviews?.map((review: any) => {
          const helpfulData = helpfulDetails?.find(
            (h: any) => h.reviewId === review.reviewId,
          );
          if (helpfulData) {
            return {
              ...review,
              isLikeFlag: helpfulData.isLikedFlag,
              likesCount: helpfulData.reviewLikeCount,
            };
          }
          return review;
        });
        setUserReviews((prev: any) => ({ ...prev, reviews: updatedReviews }));
      });
    }
  }, [userReviewDetails]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      let filterQueryParams: any = {
        pageNo:
          searchParams.get(ReviewFilterQueryParamKeys.PageNo) || undefined,
        sortBy:
          searchParams.get(ReviewFilterQueryParamKeys.SortBy) || undefined,

        rating:
          searchParams.get(ReviewFilterQueryParamKeys.Rating) || undefined,
        subjects:
          searchParams.get(ReviewFilterQueryParamKeys.Subjects) || undefined,
        keyword:
          searchParams.get(ReviewFilterQueryParamKeys.Keyword) || undefined,
        category:
          searchParams.get(ReviewFilterQueryParamKeys.Category) || undefined,
        studyLevel:
          searchParams.get(ReviewFilterQueryParamKeys.StudyLevel) || undefined,
      };

      // Remove undefined keys
      Object.keys(filterQueryParams).forEach(
        (key) =>
          filterQueryParams[key] === undefined && delete filterQueryParams[key],
      );

      try {
        const response = await getUniversityUserReviews(
          uniInfo?.institutionId,
          filterQueryParams,
        );
        const reviewIds =
          response?.reviews?.map((review: any) => review.reviewId) || [];
        setUserReviews(() => ({ ...response }));
        setLoading(false);
        if (reviewIds.length > 0) {
          const helpfulDetails = await getReviewHelpfulDetails(reviewIds);
          if (helpfulDetails?.length) {
            const updatedReviews = response.reviews.map((review: any) => {
              const helpfulData = helpfulDetails?.find(
                (h: any) => h.reviewId === review.reviewId,
              );
              if (helpfulData) {
                return {
                  ...review,
                  isLikeFlag: helpfulData.isLikedFlag,
                  likesCount: helpfulData.reviewLikeCount,
                };
              }
              return review;
            });
            setUserReviews(() => ({ ...response, reviews: updatedReviews }));
          }
        } else {
          setUserReviews(() => ({ ...response }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleRemoveRating = () => {
    const ratingValue = searchParams.get(ReviewFilterQueryParamKeys.Rating);
    const ga4Data = {
      ...commonGAAttrs,
      // event: "ga_events",
      // eventName: "ai_summarised_reviews",
      // data_label: "filter_removed",
      // data_label2: "left_nav_stars",
      // data_label3: ratingValue,
      event: GAConstants.track_event,
      event_name: "reset_filter",
      click_text: ratingValue,
    };
    GA4DataLayer(ga4Data);

    const params = new URLSearchParams(window.location.search);
    params.delete(ReviewFilterQueryParamKeys.Rating);
    window.history.replaceState(null, "", `?${params.toString()}`);
  };

  const handleRemoveKeyword = () => {
    const keywordValue = searchParams.get(ReviewFilterQueryParamKeys.Keyword);
    const ga4Data = {
      ...commonGAAttrs,
      // event: "ga_events",
      // eventName: "ai_summarised_reviews",
      // data_label: "filter_removed",
      // data_label2: "left_nav_tag",
      // data_label3: keywordValue,
      event: GAConstants.track_event,
      event_name: "reset_filter",
      click_text: keywordValue,
    };
    GA4DataLayer(ga4Data);

    const params = new URLSearchParams(window.location.search);
    params.delete(ReviewFilterQueryParamKeys.Keyword);
    window.history.replaceState(null, "", `?${params.toString()}`);
  };

  const handleResetAllGA = () => {
    const ga4Data = {
      ...commonGAAttrs,
      event: GAConstants.track_event,
      event_name: "reset_filter",
      click_text: "reset_all_filters",
    };
    GA4DataLayer(ga4Data);
  };

  const noResultTemplate = (
    <div>
      <p className="font-semibold mb-[8px]">0 Reviews found</p>
      <p>
        Sorry, we can't find any reviews with the selected filters you entered!
        Try again with a different filters and you may have more luck.
      </p>
      <Link
        href={pathname}
        onClick={handleResetAllGA}
        className="inline-flex items-center text-right justify-start mt-[24px]"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.317301 0.317301C0.740369 -0.105767 1.4263 -0.105767 1.84937 0.317301L6.5 4.96794L11.1506 0.317301C11.5737 -0.105767 12.2596 -0.105767 12.6827 0.317301C13.1058 0.740369 13.1058 1.4263 12.6827 1.84937L8.03206 6.5L12.6827 11.1506C13.1058 11.5737 13.1058 12.2596 12.6827 12.6827C12.2596 13.1058 11.5737 13.1058 11.1506 12.6827L6.5 8.03206L1.84937 12.6827C1.4263 13.1058 0.740369 13.1058 0.317301 12.6827C-0.105767 12.2596 -0.105767 11.5737 0.317301 11.1506L4.96794 6.5L0.317301 1.84937C-0.105767 1.4263 -0.105767 0.740369 0.317301 0.317301Z"
            fill="#4664DC"
          />
        </svg>
        <span className="ml-[4px] text-grey300 text-sm font-semibold font-inter hover:underline cursor-pointer text-primary-400">
          {" "}
          Reset all Filters
        </span>
      </Link>
    </div>
  );

  return (
    <>
      <div className="flex flex-col lg:grid gap-[8px] lg:grid-cols-3">
        <div className="flex lg:col-span-2 justify-between items-center">
          <div className="h5 justify-start text-grey300 font-farro">
            <svg
              className="inline-block mr-[7px]"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 7.0625C3 5.26757 4.45507 3.8125 6.25 3.8125H17.625C19.4199 3.8125 20.875 5.26757 20.875 7.0625V13.5625C20.875 15.3574 19.4199 16.8125 17.625 16.8125H14.375L9.5 21.6875V16.8125H6.25C4.45507 16.8125 3 15.3574 3 13.5625V7.0625Z"
                fill="#333333"
              />
              <path
                d="M24.125 10.3125V16.0625C24.125 18.2716 22.3341 20.0625 20.125 20.0625H15.7212L12.8503 22.9334C13.305 23.1754 13.824 23.3125 14.375 23.3125H17.625L22.5 28.1875V23.3125H25.75C27.5449 23.3125 29 21.8574 29 20.0625V13.5625C29 11.7676 27.5449 10.3125 25.75 10.3125H24.125Z"
                fill="#333333"
              />
            </svg>
            Student reviews at {uniInfo?.institutionName}?
          </div>
        </div>
        <p className="justify-start text-grey300 font-inter lg:col-span-3">
          {userReviews?.reviewCount || 0} Student reviews
        </p>
        {(searchParams.get(ReviewFilterQueryParamKeys.Rating) ||
          searchParams.get(ReviewFilterQueryParamKeys.Keyword)) && (
          <div className="flex flex-wrap gap-[8px] lg:col-span-3">
            {searchParams.get(ReviewFilterQueryParamKeys.Rating) && (
              <FilterLabel
                label={`${searchParams.get(ReviewFilterQueryParamKeys.Rating)?.replace("-star", "")} Star Reviews`}
                onRemove={handleRemoveRating}
              />
            )}
            {searchParams.get(ReviewFilterQueryParamKeys.Keyword) && (
              <FilterLabel
                label={
                  MENTION_FILTERS?.find(
                    (f) =>
                      f.value ===
                      searchParams.get(ReviewFilterQueryParamKeys.Keyword),
                  )?.label ||
                  searchParams.get(ReviewFilterQueryParamKeys.Keyword) ||
                  ""
                }
                onRemove={handleRemoveKeyword}
              />
            )}
          </div>
        )}
        <div className="flex justify-between lg:row-start-1 lg:col-start-3 lg:col-end-4 lg:justify-self-end">
          <button
            className="h-[36px] lg:hidden bg-[#c8f0f5] rounded-[20px] outline outline-1 outline-offset-[-1px] outline-zinc-800 px-[20px] py-[10px] flex justify-around w-fit items-center gap-[8px]"
            onClick={() => setFilterVisisble((prev: boolean) => !prev)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3.90625V1.875M10 3.90625C8.87817 3.90625 7.96875 4.81567 7.96875 5.9375C7.96875 7.05933 8.87817 7.96875 10 7.96875M10 3.90625C11.1218 3.90625 12.0312 4.81567 12.0312 5.9375C12.0312 7.05933 11.1218 7.96875 10 7.96875M3.90625 16.0938C5.02808 16.0938 5.9375 15.1843 5.9375 14.0625C5.9375 12.9407 5.02808 12.0312 3.90625 12.0312M3.90625 16.0938C2.78442 16.0938 1.875 15.1843 1.875 14.0625C1.875 12.9407 2.78442 12.0312 3.90625 12.0312M3.90625 16.0938V18.125M3.90625 12.0312V1.875M10 7.96875V18.125M16.0938 16.0938C17.2156 16.0938 18.125 15.1843 18.125 14.0625C18.125 12.9407 17.2156 12.0312 16.0938 12.0312M16.0938 16.0938C14.9719 16.0938 14.0625 15.1843 14.0625 14.0625C14.0625 12.9407 14.9719 12.0312 16.0938 12.0312M16.0938 16.0938V18.125M16.0938 12.0312V1.875"
                stroke="#333333"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <small className="font-bold text-[14px]">
              Filter{" "}
              {(() => {
                let count = 0;
                if (searchParams.get(ReviewFilterQueryParamKeys.Rating))
                  count++;
                if (searchParams.get(ReviewFilterQueryParamKeys.Keyword))
                  count++;
                if (searchParams.get(ReviewFilterQueryParamKeys.Subjects))
                  count++;
                if (searchParams.get(ReviewFilterQueryParamKeys.Category))
                  count++;
                return count > 0 ? `(${count})` : "";
              })()}
            </small>
          </button>
          <div className="self-center">
            <ReviewSort commonGAAttrs={commonGAAttrs} />
          </div>
        </div>
      </div>
      <>
        {isLoading ? (
          <DetailedReviewLightBoxSkeleton />
        ) : (
          <>
            {userReviews?.reviews?.length ? (
              <>
                {userReviews?.reviews?.map(
                  (review: any, index: number, array: any) => (
                    <React.Fragment key={review?.reviewId || index}>
                      <DetailedReviewContents
                        idx={index}
                        review={review}
                        commonGAAttrs={commonGAAttrs}
                        commonGAAttrsOld={commonGAAttrsOld}
                      />
                      {index < array.length - 1 && <hr />}
                    </React.Fragment>
                  ),
                )}
              </>
            ) : (
              noResultTemplate
            )}
            {userReviews?.reviewCount > REVIEWS_PER_PAGE && (
              <ReviewsPaginations
                totalPages={Math.ceil(
                  userReviews?.reviewCount / REVIEWS_PER_PAGE,
                )}
                initialPage={searchParams.get("pageno") || defaultPageNo}
              />
            )}
          </>
        )}
      </>
    </>
  );
};
export default UserReviewList;
